import React, { useMemo } from 'react';
import FilterIcon from '../icons/FilterIcon';
import CarretDown from '../icons/CarretDown';
import SearchIcon from '../icons/SearchIcon';
import truncateText from '../../utils/truncateText';

type ICalendarFilter = {
  searchItems: string[] | null;
  handleApply: (selectedItem: any, isClear?: any) => void;
  selectedItems: string[];
  setSelectedItems: any;
  search: string;
  setSearch: (e: string) => void;
  dropdownRef: any;
  dropdownOpen: boolean;
  setDropdownOpen: (e: boolean) => void;
  label?: string;
  alignRight?: boolean;
};

const DropDownFilter: React.FC<ICalendarFilter> = ({
  searchItems,
  handleApply,
  selectedItems,
  setSelectedItems,
  search,
  setSearch,
  dropdownRef,
  dropdownOpen,
  setDropdownOpen,
  label = 'Filter',
  alignRight = false,
}) => {
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleCheckboxChange = (value: string) => {
    const isSelected = selectedItems.includes(value);

    if (isSelected) {
      setSelectedItems((prevSelected: any[]) =>
        prevSelected.filter((item: string) => item !== value),
      );
    } else {
      setSelectedItems((prevSelected: any) => [...prevSelected, value]);
    }
  };

  const handleClearSearch = () => {
    setSelectedItems([]);
    setSearch('');
  };

  const itemsList = useMemo(() => {
    const noOfKits = searchItems;
    if (search && noOfKits?.length) {
      const needle = search.toLowerCase();
      return noOfKits.filter((value) =>
        value.toString().toLowerCase().includes(needle),
      );
    }
    return noOfKits;
  }, [search, searchItems]);

  const summary =
    // eslint-disable-next-line no-nested-ternary
    selectedItems?.length === 1
      ? truncateText(selectedItems[0], 18)
      : selectedItems?.length > 1
      ? `${selectedItems.length} selected`
      : label;

  return (
    <div className="filter-dd-wrap" ref={dropdownRef}>
      <button
        type="button"
        className={`filter-input filter-dd ${
          selectedItems?.length ? 'is-set' : ''
        }`}
        onClick={toggleDropdown}
        aria-haspopup="listbox"
        aria-expanded={dropdownOpen}
      >
        <FilterIcon />
        <span className="filter-dd-label">{summary}</span>
        {selectedItems?.length ? (
          <span
            className="filter-clear"
            role="button"
            tabIndex={0}
            aria-label={`Clear ${label} filter`}
            onClick={(e) => {
              e.stopPropagation();
              handleApply([], true);
              handleClearSearch();
              setDropdownOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                handleApply([], true);
                handleClearSearch();
                setDropdownOpen(false);
              }
            }}
          >
            ×
          </span>
        ) : (
          <CarretDown />
        )}
      </button>
      {dropdownOpen && (
        <div
          className="filter-popover"
          style={alignRight ? { left: 'auto', right: 0 } : undefined}
        >
          <div className="filter-input">
            <SearchIcon width="14" height="14" />
            <input
              type="text"
              placeholder={`Search ${label.toLowerCase()}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="filter-popover-list scroll-bar-filter">
            {!itemsList?.length
              ? 'No item found'
              : itemsList?.map((v, index) => (
                  <label className="filter-option" key={`${v}-${index}`}>
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(v.toString())}
                      onChange={() => handleCheckboxChange(v.toString())}
                    />
                    {v}
                  </label>
                ))}
          </div>
          <div className="filter-popover-actions">
            <span
              onClick={() => {
                handleClearSearch();
              }}
              role="button"
              tabIndex={0}
              className="reset-button"
            >
              Reset
            </span>
            <span
              onClick={() => {
                handleApply(selectedItems);
                setDropdownOpen(false);
              }}
              role="button"
              tabIndex={0}
              className="apply-button"
            >
              Apply
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropDownFilter;
